import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import mammoth from 'mammoth';
import './FileViewerModal.css';

/**
 * FileViewerModal Component
 * Smart file viewer for multiple files with different types
 * Supports: Images, PDFs, Word, Excel documents
 */
function FileViewerModal({ files, initialIndex = 0, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(100);
  const [showThumbnails, setShowThumbnails] = useState(true);
  const [excelData, setExcelData] = useState(null);
  const [wordHTML, setWordHTML] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Image viewer advanced features
  const [rotation, setRotation] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Excel viewer features
  const [currentSheet, setCurrentSheet] = useState(0);
  const [excelSearchTerm, setExcelSearchTerm] = useState('');

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);

  const currentFile = files && files.length > 0 ? files[currentIndex] : null;

  // Determine file type
  const getFileType = (filename) => {
    if (!filename) return 'other';
    const ext = filename.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) {
      return 'image';
    } else if (ext === 'pdf') {
      return 'pdf';
    } else if (['doc', 'docx'].includes(ext)) {
      return 'word';
    } else if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return 'excel';
    }
    return 'other';
  };

  const getFileIcon = (filename) => {
    const type = getFileType(filename);
    const icons = {
      image: '🖼️',
      pdf: '📄',
      word: '📝',
      excel: '📊',
      other: '📎'
    };
    return icons[type] || icons.other;
  };

  const currentFileType = currentFile ? getFileType(currentFile.file_name) : 'other';

  const resetViewerStates = () => {
    setZoom(100);
    setRotation(0);
    setPanPosition({ x: 0, y: 0 });
    setExcelSearchTerm('');
    setCurrentSheet(0);
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : files.length - 1));
    resetViewerStates();
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < files.length - 1 ? prev + 1 : 0));
    resetViewerStates();
  };

  const handleThumbnailClick = (index) => {
    setCurrentIndex(index);
    resetViewerStates();
  };

  // Handle keyboard navigation
  const handleKeyPress = React.useCallback((e) => {
    if (e.key === 'ArrowLeft') handlePrevious();
    if (e.key === 'ArrowRight') handleNext();
    if (e.key === 'Escape') onClose();
  }, [currentIndex, files, onClose]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  // Load Excel or Word file
  useEffect(() => {
    const loadFile = async () => {
      if (!currentFile) return;

      const fileType = getFileType(currentFile.file_name);

      // Reset previous data
      setExcelData(null);
      setWordHTML(null);

      if (fileType === 'excel') {
        setIsLoading(true);
        try {
          const response = await fetch(currentFile.download_url);
          const arrayBuffer = await response.arrayBuffer();
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });

          // Get first sheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          setExcelData({
            sheetNames: workbook.SheetNames,
            currentSheet: firstSheetName,
            data: jsonData,
            workbook: workbook
          });
        } catch (error) {
          console.error('Error loading Excel file:', error);
        }
        setIsLoading(false);
      } else if (fileType === 'word') {
        setIsLoading(true);
        try {
          const response = await fetch(currentFile.download_url);
          const arrayBuffer = await response.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          setWordHTML(result.value);
        } catch (error) {
          console.error('Error loading Word file:', error);
        }
        setIsLoading(false);
      }
    };

    loadFile();
  }, [currentFile]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 25, 300));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 25, 25));
  };

  const handleZoomSlider = (e) => {
    setZoom(parseInt(e.target.value));
  };

  const handleFitToScreen = () => {
    setZoom(100);
    setPanPosition({ x: 0, y: 0 });
  };

  const handleActualSize = () => {
    setZoom(100);
    setRotation(0);
    setPanPosition({ x: 0, y: 0 });
  };

  const handleRotateLeft = () => {
    setRotation((prev) => (prev - 90) % 360);
  };

  const handleRotateRight = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  // Image panning handlers
  const handleMouseDown = (e) => {
    if (zoom > 100) {
      setIsPanning(true);
      setDragStart({
        x: e.clientX - panPosition.x,
        y: e.clientY - panPosition.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning && zoom > 100) {
      setPanPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  // Excel sheet switching
  const handleSheetChange = (sheetIndex) => {
    if (!excelData || !excelData.workbook) return;

    const sheetName = excelData.workbook.SheetNames[sheetIndex];
    const worksheet = excelData.workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    setExcelData({
      ...excelData,
      currentSheet: sheetName,
      data: jsonData
    });
    setCurrentSheet(sheetIndex);
  };

  const handleDownloadCurrent = () => {
    window.open(currentFile.download_url, '_blank');
  };

  const handleDownloadAll = () => {
    files.forEach((file, index) => {
      setTimeout(() => {
        window.open(file.download_url, '_blank');
      }, index * 500); // Delay to prevent browser blocking
    });
  };

  // Fullscreen toggle
  const handleFullscreenToggle = () => {
    const modalElement = document.querySelector('.file-viewer-modal');

    if (!isFullscreen) {
      // Enter fullscreen
      if (modalElement.requestFullscreen) {
        modalElement.requestFullscreen();
      } else if (modalElement.webkitRequestFullscreen) {
        modalElement.webkitRequestFullscreen();
      } else if (modalElement.mozRequestFullScreen) {
        modalElement.mozRequestFullScreen();
      } else if (modalElement.msRequestFullscreen) {
        modalElement.msRequestFullscreen();
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  // Listen for fullscreen changes
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Early return if no files
  if (!files || files.length === 0 || !currentFile) {
    return null;
  }

  return (
    <div className="file-viewer-modal" onClick={onClose}>
      <div className="file-viewer-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="file-viewer-header">
          <div className="file-viewer-title">
            <span className="file-icon">{getFileIcon(currentFile.file_name)}</span>
            <div className="file-title-info">
              <h3>{currentFile.file_name}</h3>
              <p>{currentIndex + 1} / {files.length}</p>
            </div>
          </div>
          <div className="file-viewer-actions">
            {files.length > 1 && (
              <button
                onClick={() => setShowThumbnails(!showThumbnails)}
                className="action-btn-compact"
                title={showThumbnails ? 'Hide thumbnails' : 'Show thumbnails'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7"/>
                  <rect x="14" y="3" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/>
                  <rect x="14" y="14" width="7" height="7"/>
                </svg>
              </button>
            )}
            {currentFileType === 'image' && (
              <>
                <button onClick={handleRotateLeft} className="action-btn-compact" title="Rotate left">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                    <path d="M3 3v5h5"/>
                  </svg>
                </button>
                <button onClick={handleRotateRight} className="action-btn-compact" title="Rotate right">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12a9 9 0 1 1-9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                    <path d="M21 3v5h-5"/>
                  </svg>
                </button>
                <button onClick={handleZoomOut} className="action-btn-compact" title="Zoom out">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    <line x1="8" y1="11" x2="14" y2="11"/>
                  </svg>
                </button>
                <span className="zoom-level">{zoom}%</span>
                <button onClick={handleZoomIn} className="action-btn-compact" title="Zoom in">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    <line x1="11" y1="8" x2="11" y2="14"/>
                    <line x1="8" y1="11" x2="14" y2="11"/>
                  </svg>
                </button>
                <button onClick={handleActualSize} className="action-btn-compact" title="Reset view">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                    <path d="M8 16H3v5"/>
                    <path d="M16 8h5V3"/>
                  </svg>
                </button>
              </>
            )}
            <button onClick={handleDownloadCurrent} className="action-btn-compact" title="Download this file">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
            </button>
            {files.length > 1 && (
              <button onClick={handleDownloadAll} className="action-btn-compact" title="Download all files">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                <span className="btn-label">All</span>
              </button>
            )}
            <button onClick={handleFullscreenToggle} className="action-btn-compact" title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
              {isFullscreen ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                </svg>
              )}
            </button>
            <button onClick={onClose} className="action-btn-compact close-btn" title="Close">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="file-viewer-body">
          {/* Thumbnails Sidebar */}
          {showThumbnails && files.length > 1 && (
            <div className="thumbnails-sidebar">
              <h4>Files ({files.length})</h4>
              <div className="thumbnails-list">
                {files.map((file, index) => (
                  <div
                    key={index}
                    className={`thumbnail-item ${index === currentIndex ? 'active' : ''}`}
                    onClick={() => handleThumbnailClick(index)}
                  >
                    <div className="thumbnail-icon">{getFileIcon(file.file_name)}</div>
                    <div className="thumbnail-info">
                      <div className="thumbnail-name">{file.file_name}</div>
                      <div className="thumbnail-size">{file.size_formatted || ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Viewer Area */}
          <div className="viewer-area">
            {/* Navigation Buttons */}
            {files.length > 1 && (
              <>
                <button
                  className="nav-btn prev-btn"
                  onClick={handlePrevious}
                  title="Previous file (←)"
                >
                  ←
                </button>
                <button
                  className="nav-btn next-btn"
                  onClick={handleNext}
                  title="Next file (→)"
                >
                  →
                </button>
              </>
            )}

            {/* File Content */}
            <div className="viewer-content">
              {currentFileType === 'image' && (
                <div className="image-viewer-modern">
                  <div
                    className="image-viewer-container"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                  >
                    <div
                      className="image-transform-wrapper"
                      style={{
                        transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${zoom / 100}) rotate(${rotation}deg)`,
                        cursor: zoom > 100 ? (isPanning ? 'grabbing' : 'grab') : 'default',
                        transition: isPanning ? 'none' : 'transform 0.2s ease'
                      }}
                    >
                      <img
                        src={currentFile.download_url}
                        alt={currentFile.file_name}
                        className="zoomable-image"
                        draggable={false}
                      />
                    </div>
                  </div>
                  {/* Zoom Slider Overlay */}
                  <div className="zoom-slider-overlay">
                    <button onClick={handleZoomOut} className="slider-btn" title="Zoom out">−</button>
                    <input
                      type="range"
                      min="25"
                      max="300"
                      step="5"
                      value={zoom}
                      onChange={handleZoomSlider}
                      className="zoom-slider"
                    />
                    <button onClick={handleZoomIn} className="slider-btn" title="Zoom in">+</button>
                    <span className="slider-zoom-display">{zoom}%</span>
                  </div>
                </div>
              )}

              {currentFileType === 'pdf' && (
                <div className="pdf-viewer">
                  <iframe
                    src={`${currentFile.download_url}#toolbar=1`}
                    title={currentFile.file_name}
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                  />
                </div>
              )}

              {currentFileType === 'word' && (
                <div className="document-viewer-advanced">
                  {isLoading ? (
                    <div className="loading-spinner">
                      <div className="spinner"></div>
                      <p>Loading Word document...</p>
                    </div>
                  ) : wordHTML ? (
                    <div className="word-preview">
                      <div className="document-toolbar">
                        <span className="doc-label">📝 Word Document Preview</span>
                        <button onClick={handleDownloadCurrent} className="toolbar-download-btn">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                          </svg>
                          Download Original
                        </button>
                      </div>
                      <div className="word-content" dangerouslySetInnerHTML={{ __html: wordHTML }} />
                    </div>
                  ) : (
                    <div className="document-viewer-compact">
                      <div className="file-preview-card">
                        <div className="file-icon-large">{getFileIcon(currentFile.file_name)}</div>
                        <div className="file-details">
                          <h3 className="file-name">{currentFile.file_name}</h3>
                          <p className="file-type">Microsoft Word Document</p>
                          {currentFile.size_formatted && (
                            <p className="file-size">{currentFile.size_formatted}</p>
                          )}
                        </div>
                        <button onClick={handleDownloadCurrent} className="download-btn-compact">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                          </svg>
                          Download File
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {currentFileType === 'excel' && (
                <div className="document-viewer-advanced">
                  {isLoading ? (
                    <div className="loading-spinner">
                      <div className="spinner"></div>
                      <p>Loading Excel spreadsheet...</p>
                    </div>
                  ) : excelData ? (
                    <div className="excel-preview">
                      <div className="document-toolbar">
                        <div className="toolbar-left">
                          <span className="doc-label">📊 Excel Spreadsheet Preview</span>
                          {excelData.workbook.SheetNames.length > 1 && (
                            <div className="sheet-tabs">
                              {excelData.workbook.SheetNames.map((sheetName, index) => (
                                <button
                                  key={index}
                                  onClick={() => handleSheetChange(index)}
                                  className={`sheet-tab ${currentSheet === index ? 'active' : ''}`}
                                >
                                  {sheetName}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="toolbar-right">
                          <input
                            type="text"
                            placeholder="🔍 Search in table..."
                            value={excelSearchTerm}
                            onChange={(e) => setExcelSearchTerm(e.target.value)}
                            className="excel-search-input"
                          />
                          <button onClick={handleDownloadCurrent} className="toolbar-download-btn">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                              <polyline points="7 10 12 15 17 10"/>
                              <line x1="12" y1="15" x2="12" y2="3"/>
                            </svg>
                            Download
                          </button>
                        </div>
                      </div>
                      <div className="excel-content">
                        <table className="excel-table">
                          <tbody>
                            {excelData.data
                              .filter(row => {
                                if (!excelSearchTerm) return true;
                                return row.some(cell =>
                                  String(cell).toLowerCase().includes(excelSearchTerm.toLowerCase())
                                );
                              })
                              .map((row, rowIndex) => (
                              <tr key={rowIndex}>
                                {row.map((cell, cellIndex) => {
                                  const cellValue = cell !== null && cell !== undefined ? String(cell) : '';
                                  const isHighlighted = excelSearchTerm &&
                                    cellValue.toLowerCase().includes(excelSearchTerm.toLowerCase());
                                  return (
                                    <td key={cellIndex} className={isHighlighted ? 'highlighted-cell' : ''}>
                                      {cellValue}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="document-viewer-compact">
                      <div className="file-preview-card">
                        <div className="file-icon-large">{getFileIcon(currentFile.file_name)}</div>
                        <div className="file-details">
                          <h3 className="file-name">{currentFile.file_name}</h3>
                          <p className="file-type">Microsoft Excel Spreadsheet</p>
                          {currentFile.size_formatted && (
                            <p className="file-size">{currentFile.size_formatted}</p>
                          )}
                        </div>
                        <button onClick={handleDownloadCurrent} className="download-btn-compact">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                          </svg>
                          Download File
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {currentFileType === 'other' && (
                <div className="document-viewer-compact">
                  <div className="file-preview-card">
                    <div className="file-icon-large">{getFileIcon(currentFile.file_name)}</div>
                    <div className="file-details">
                      <h3 className="file-name">{currentFile.file_name}</h3>
                      <p className="file-type">Document File</p>
                      {currentFile.size_formatted && (
                        <p className="file-size">{currentFile.size_formatted}</p>
                      )}
                    </div>
                    <button onClick={handleDownloadCurrent} className="download-btn-compact">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      Download File
                    </button>
                  </div>
                  <div className="preview-note">
                    <p>📄 Preview not available for this file type. Click download to view.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer with keyboard shortcuts */}
        <div className="file-viewer-footer">
          <div className="keyboard-shortcuts">
            <span>💡 Shortcuts: </span>
            <kbd>←</kbd> Previous
            <kbd>→</kbd> Next
            <kbd>Esc</kbd> Close
          </div>
        </div>
      </div>
    </div>
  );
}

export default FileViewerModal;
